<template>
  <div class="container-fluid d-flex flex-column h-100 onboarding-container proposal-container">
    <div class="row m-4 justify-content-center onboarding-new align-items-stretch">
      <!-- Left content column -->
      <div class="col-lg-9 col-12">
        <div class="row h-100 justify-content-center align-items-center flex-grow-1">
          <!-- Logo - visible on all screen sizes -->
          <div class="container-fluid col-12">
            <a class="navbar-brand d-none d-lg-block" href="/">
              <img src="/img/logo_png/avid_logo_black.png" alt="Avid" class="navbar-brand-logo-avidonly px-2" />
            </a>
            <div class="d-lg-none d-flex align-items-center justify-content-center">
              <img src="/img/logo_png/avid_logo_red.png" alt="Avid" class="w-25" style="max-width: 250px;">
            </div>
          </div>

          <div class="container-fluid col-12 px-5 mt-4">
              <h2 class="text-center mb-2">You’ve officially begun your Avid Journey!</h2>
              <h4 class="text-center mb-4">Next step: Set up your payment information</h4>

              <!-- Billing contact -->
              <h5>Billing Contact</h5>
              <form @submit.prevent="saveBillingContact">
                <div class="row mb-3">
                  <div class="col-md-6">
                    <label class="form-label">First Name *</label>
                    <input v-model="contact.first_name" class="form-control" aria-label="First Name *">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Last Name *</label>
                    <input v-model="contact.last_name" class="form-control" aria-label="Last Name *">
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-md-6">
                    <label class="form-label">Email *</label>
                    <input v-model="contact.email" type="email" class="form-control" aria-label="Email *">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Phone</label>
                    <input v-model="contact.phone" class="form-control" aria-label="Phone">
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label">Address *</label>
                  <input v-model="contact.address" class="form-control" aria-label="Address *">
                </div>
                <div class="row mb-3">
                  <div class="col-md-4">
                    <label class="form-label">City *</label>
                    <input v-model="contact.city" class="form-control" aria-label="City *">
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">State/Region *</label>
                    <input v-model="contact.region" class="form-control" aria-label="State/Region *">
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Postal Code *</label>
                    <input v-model="contact.postal_code" class="form-control" aria-label="Postal Code *">
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label">Country *</label>
                  <select v-model="contact.country" class="form-select" aria-label="Country *">
                    <option v-for="c in allCountries" :value="c[1]" :key="c[1]">{{ c[0] }}</option>
                  </select>
                </div>
              </form>

              <!-- Payment Method Selection -->
              <h5 class="mt-5">Payment Method</h5>
              <div>
                  <!-- Stripe Option -->
                  <div class="payment-option mb-3" :class="{ 'selected': selectedPaymentMethod === 'stripe' }">
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="radio" 
                        name="paymentMethod" 
                        id="stripeOption" 
                        value="stripe" 
                        v-model="selectedPaymentMethod"
                        @change="showAlternativeOptions = false"
                       aria-label="Paymentmethod">
                      <label class="form-check-label w-100" for="stripeOption">
                        <div class="d-flex align-items-center">
                          <i class="fab fa-stripe fa-2x text-primary me-3"></i>
                          <div>
                            <strong>Credit/ACH via Stripe</strong>
                            <div class="text-muted small">Secure online payment processing</div>
                          </div>
                        </div>
                      </label>
                    </div>
                    
                    <div class="mt-3">
                      <div id="stripe-element"></div>
                    </div>
                  </div>

                  <!-- Alternative Options Toggle -->
                  <div v-if="monthlyRecurringRevenue > 1500">
                    <button 
                      type="button" 
                      class="btn btn-secondary"
                      @click="showAlternativeOptions = !showAlternativeOptions"
                    >
                      <i class="fas fa-chevron-down me-1" :class="{ 'fa-chevron-up': showAlternativeOptions }"></i>
                      {{ showAlternativeOptions ? 'Hide' : 'Show' }} Alternative Payment Options
                    </button>
                  </div>

                  <!-- Alternative Payment Options (Collapsible) -->
                  <div v-if="showAlternativeOptions && monthlyRecurringRevenue > 1500" class="mt-3">
                    <hr>
                    <h6 class="text-muted mb-3">Alternative Payment Methods</h6>
                    
                    <!-- Bill.com Option -->
                    <div class="payment-option mb-3" :class="{ 'selected': selectedPaymentMethod === 'bill.com' }">
                      <div class="form-check">
                        <input 
                          class="form-check-input" 
                          type="radio" 
                          name="paymentMethod" 
                          id="billcomOption" 
                          value="bill.com" 
                          v-model="selectedPaymentMethod"
                         aria-label="Paymentmethod">
                        <label class="form-check-label w-100" for="billcomOption">
                          <div class="d-flex align-items-center">
                            <div class="payment-icon me-3">
                              <i class="fas fa-building text-info fa-2x"></i>
                            </div>
                            <div>
                              <strong>Bill.com</strong>
                              <div class="text-muted small">Enterprise billing platform</div>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>

                    <!-- Pushpay ACH Option -->
                    <div class="payment-option mb-3" :class="{ 'selected': selectedPaymentMethod === 'pushpay' }">
                      <div class="form-check">
                        <input 
                          class="form-check-input" 
                          type="radio" 
                          name="paymentMethod" 
                          id="pushpayOption" 
                          value="pushpay" 
                          v-model="selectedPaymentMethod"
                         aria-label="Paymentmethod">
                        <label class="form-check-label w-100" for="pushpayOption">
                          <div class="d-flex align-items-center">
                            <div class="payment-icon me-3">
                              <i class="fas fa-university text-success fa-2x"></i>
                            </div>
                            <div>
                              <strong>Pushpay ACH</strong>
                              <div class="text-muted small">Direct bank transfer</div>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>

                    <!-- Other Methods Option -->
                    <div class="payment-option mb-3" :class="{ 'selected': selectedPaymentMethod === 'other' }">
                      <div class="form-check">
                        <input 
                          class="form-check-input" 
                          type="radio" 
                          name="paymentMethod" 
                          id="otherOption" 
                          value="other" 
                          v-model="selectedPaymentMethod"
                         aria-label="Paymentmethod">
                        <label class="form-check-label w-100" for="otherOption">
                          <div class="d-flex align-items-center">
                            <div class="payment-icon me-3">
                              <i class="fas fa-cog text-warning fa-2x"></i>
                            </div>
                            <div>
                              <strong>Other Payment Methods</strong>
                              <div class="text-muted small">Custom billing arrangements</div>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div class="alert alert-info">
                      <i class="fas fa-info-circle me-2"></i>
                      <strong>Need help?</strong> LockStep support will be reaching out within the next 48 hours to assist with setup.
                    </div>
                  </div>

                  <!-- Save Button -->
                  <button 
                    class="btn btn-primary w-100 mt-3" 
                    @click="saveAll" 
                    :disabled="saving"
                  >
                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                    Save 
                  </button>
              </div>
          </div>
        </div>
      </div>

      <!-- Right image column -->
      <div class="col-lg-3 d-none d-lg-block bg-with-image"></div>
    </div>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { allCountries } from 'country-region-data';
import Swal from 'sweetalert2';
export default {
  name: 'payment-page',
  props: {
    proposal: Object,
    entity: Object,
    entityType: String,
    user: Object,
  },
  data(){
    return {
      local_proposal: this.local_proposal,
      allCountries: allCountries,
      contact: {
        first_name: this.user?.first_name || '',
        last_name: this.user?.last_name || '',
        email: this.user?.email || '',
        phone: '',
        address: this.entity.billing_contact?.address || '',
        city: this.entity.billing_contact?.city || '',
        region: this.entity.billing_contact?.region || '',
        postal_code: this.entity.billing_contact?.postal_code || '',
        country: this.entity.billing_contact?.country || 'US'
      },
      loadingStripe: true,
      stripe: null,
      elements: null,
      saving: false,
      selectedPaymentMethod: 'stripe', // 'stripe', 'bill.com', 'pushpay', 'other'
      showAlternativeOptions: false,
    }
  },
  computed: {
    monthlyRecurringRevenue() {
      if (!this.proposal?.line_items) return 0;
      
      return this.proposal.line_items.reduce((total, item) => {
        // Skip one-time charges
        if (item.billing_frequency === 'once') {
          return total;
        }
        
        // Calculate monthly revenue for this line item
        const netAmount = Number(item.net_amount) || 0;
        const frequency = item.billing_frequency?.toLowerCase() || 'monthly';
        
        let monthlyAmount = netAmount;
        switch (frequency) {
          case 'monthly':
            monthlyAmount = netAmount;
            break;
          case 'quarterly':
            monthlyAmount = netAmount / 3;
            break;
          case 'annually':
          case 'yearly':
            monthlyAmount = netAmount / 12;
            break;
          case 'semi-annually':
            monthlyAmount = netAmount / 6;
            break;
          default:
            monthlyAmount = netAmount; // Default to monthly if unknown
        }
        
        return total + monthlyAmount;
      }, 0);
    }
  },
  async mounted(){
    await this.initializeStripe();
  },
  watch: {
    selectedPaymentMethod: {
      async handler(newMethod) {
        if (newMethod === 'stripe' && this.stripe && this.elements) {
          await this.$nextTick();
          this.mountStripeElement();
        }
      },
      immediate: false
    }
  },
  methods:{
    async initializeStripe() {
      try {
        const stripeKey = document.head.querySelector('meta[name="stripe-key"]').content;
        this.stripe = await loadStripe(stripeKey);
        const { data } = await axios.post(`/api/billing/payment-method/${this.entityType}/${this.entity.id}/intent`);
        const clientSecret = data.client_secret;
        this.elements = this.stripe.elements({ 
          clientSecret, 
          appearance: {
            theme: 'stripe',
            variables: {
              borderRadius: '20px'
            },
            rules: {
              '.AccordionItem, .p-AccordionItem': {
                borderRadius: '20px'
              }
            }
          }
        });
        
        // If stripe is already selected, mount the element
        if (this.selectedPaymentMethod === 'stripe') {
          await this.$nextTick();
          this.mountStripeElement();
        }
        
        this.loadingStripe = false;
      } catch (error) {
        console.error('Failed to initialize Stripe:', error);
        this.loadingStripe = false;
      }
    },
    
    mountStripeElement() {
      try {
        // Check if element exists and hasn't been mounted already
        const element = document.getElementById('stripe-element');
        if (element && !element.hasChildNodes()) {
          const paymentElement = this.elements.create('payment');
          paymentElement.mount('#stripe-element');
        }
      } catch (error) {
        console.error('Failed to mount Stripe element:', error);
      }
    },
    
    isValidEmail(email){const re=/^[^\s@]+@[^\s@]+\.[^\s@]+$/;return re.test(email)},
    isValidRegion(countryCode, regionInput) {
      if (!countryCode || !regionInput) return false;
      const country = allCountries.find(c => (c[1] || '').toLowerCase() === countryCode.toLowerCase());
      if (!country) return false;
      const regionsArr = country[2] || [];
      const input = regionInput.trim().toLowerCase();
      return regionsArr.some(r => {
        const rName = (r[0] || '').toLowerCase();
        const rCode = (r[1] || '').toLowerCase();
        return input === rName || input === rCode;
      });
    },
    validateContact(){
      const c = this.contact;
      if (!c.first_name?.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'First name is required',
          icon: 'error',
          customClass: { popup: 'swal-error' }
        });
        return false;
      }
      if (!c.last_name?.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Last name is required',
          icon: 'error',
          customClass: { popup: 'swal-error' }
        });
        return false;
      }
      if (!c.email?.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Email is required',
          icon: 'error',
          customClass: { popup: 'swal-error' }
        });
        return false;
      }
      if (!this.isValidEmail(c.email)) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Please enter a valid email address',
          icon: 'error',
          customClass: { popup: 'swal-error' }
        });
        return false;
      }
      if (!c.address?.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Address is required',
          icon: 'error',
          customClass: { popup: 'swal-error' }
        });
        return false;
      }
      if (!c.city?.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'City is required',
          icon: 'error',
          customClass: { popup: 'swal-error' }
        });
        return false;
      }
      if (!c.region?.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'State/Region is required',
          icon: 'error',
          customClass: { popup: 'swal-error' }
        });
        return false;
      }
      if (!c.postal_code?.trim()) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Postal Code is required',
          icon: 'error',
          customClass: { popup: 'swal-error' }
        });
        return false;
      }
      if (!this.isValidRegion(c.country, c.region)) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Region / State is not valid for the selected country',
          icon: 'error',
          customClass: { popup: 'swal-error' }
        });
        return false;
      }
      return true;
    },
    async saveAll(){
      // Validate contact first
      if(!this.validateContact()) return;
      
      this.saving = true;
      try{
        // Save billing contact
        const method = this.entity.billing_contact ? 'put':'post';
        await axios({method, url:`/api/billing/contact/${this.entityType}/${this.entity.id}`, data:this.contact});
        
        // Save payment method
        let paymentData = {
          payment_method: this.selectedPaymentMethod
        };

        // Handle Stripe payment method
        if (this.selectedPaymentMethod === 'stripe') {
          const { error, setupIntent } = await this.stripe.confirmSetup({ elements:this.elements, redirect:'if_required' });
          if(error){ 
            Swal.fire({
              title: 'Payment Error',
              text: error.message,
              icon: 'error',
              customClass: { popup: 'swal-error' }
            });
            this.saving = false;
            return; 
          }
          paymentData.payment_method_id = setupIntent.payment_method;
        }

        // Send payment method selection to server
        await axios.post(`/api/billing/payment-method/${this.entityType}/${this.entity.id}`, paymentData);
        
        // Show success modal with appropriate message
        const successMessage = this.selectedPaymentMethod === 'stripe' 
          ? 'Contact and payment information saved!'
          : `Contact and payment method (${this.getPaymentMethodDisplayName()}) saved!`;
          
        await Swal.fire({
          title: successMessage,
          text: `You will be redirected to your settings page to begin connecting your systems. Additionally, LockStep support will be reaching out within the next 48 hours.`,
          icon: 'success',
          confirmButtonText: 'Continue to Settings',
          confirmButtonColor: '#198754',
          customClass: { popup: 'swal-success' }
        });
        
        //If it is an agency, redirect to the agency settings page
        if(this.entityType === 'agency' && this.proposal.client_id) {
          window.location = '/settings/' + this.proposal.client.url + '/integrations';
        }
        else if(this.entityType === 'agency' && !this.proposal.client_id) {
          window.location = '/';
        }
        //If it is a client, redirect to the client settings page
        else if(this.entityType === 'client') {
          window.location = '/settings/' + this.entity.url + '/integrations';
        }
      }catch(e){ 

        Swal.fire('Error', 'Failed to save contact and payment method', 'error');
      }
      this.saving = false;
    },
    
    getPaymentMethodDisplayName() {
      const methodNames = {
        'stripe': 'Credit/Debit Card',
        'bill.com': 'Bill.com',
        'pushpay': 'Pushpay ACH',
        'other': 'Custom Payment Method'
      };
      return methodNames[this.selectedPaymentMethod] || this.selectedPaymentMethod;
    }
  }
}
</script>

